import React, { useState, useEffect, useRef } from 'react';
import { leadAPI } from '../api';

function LeadFinder() {
  const [form, setForm] = useState({
    api_key: '',
    cities: '',
    keywords: '',
    max_per_query: 8,
    scrape_emails: true,
  });

  const [status, setStatus] = useState({
    running: false,
    progress: '',
    done: 0,
    total: 0,
    found: 0,
    error: '',
  });

  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ new: 0, with_email: 0, imported: 0 });
  const [selected, setSelected] = useState([]); // array of lead ids

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const pollRef = useRef(null);

  useEffect(() => {
    loadList();
    refreshStatus();
    // 组件卸载时清理轮询
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMessage = (type, text, ms = 3000) => {
    setMessage({ type, text });
    if (ms) setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  const loadList = async () => {
    try {
      const res = await leadAPI.list('new');
      const payload = res.data?.data || [];
      setLeads(payload);
      setCounts(res.data?.counts || { new: 0, with_email: 0, imported: 0 });
    } catch (error) {
      console.error('加载线索列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      const res = await leadAPI.status();
      const s = res.data?.data || {};
      setStatus({
        running: !!s.running,
        progress: s.progress || '',
        done: s.done || 0,
        total: s.total || 0,
        found: s.found || 0,
        error: s.error || '',
      });
      if (s.running) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (error) {
      console.error('获取查找状态失败:', error);
    }
  };

  const startPolling = () => {
    if (pollRef.current) return; // 已在轮询
    pollRef.current = setInterval(async () => {
      try {
        const res = await leadAPI.status();
        const s = res.data?.data || {};
        setStatus({
          running: !!s.running,
          progress: s.progress || '',
          done: s.done || 0,
          total: s.total || 0,
          found: s.found || 0,
          error: s.error || '',
        });
        if (!s.running) {
          stopPolling();
          // 查找结束后刷新候选列表
          loadList();
          if (s.error) {
            showMessage('error', '查找出错：' + s.error, 6000);
          } else {
            showMessage('success', `查找完成，共找到 ${s.found || 0} 条线索`);
          }
        }
      } catch (error) {
        console.error('轮询状态失败:', error);
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const splitCsv = (str) =>
    (str || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const handleStart = async (e) => {
    e.preventDefault();
    try {
      await leadAPI.find({
        api_key: form.api_key || undefined,
        cities: splitCsv(form.cities),
        keywords: splitCsv(form.keywords),
        max_per_query: Number(form.max_per_query) || 8,
        scrape_emails: form.scrape_emails,
      });
      showMessage('success', '已开始查找，正在后台运行...');
      setStatus((prev) => ({ ...prev, running: true, error: '' }));
      startPolling();
    } catch (error) {
      showMessage('error', '启动查找失败：' + (error.response?.data?.message || error.message), 6000);
    }
  };

  const handleStop = async () => {
    try {
      await leadAPI.stop();
      showMessage('info', '已发送停止指令');
      // 立即刷新一次状态
      refreshStatus();
    } catch (error) {
      showMessage('error', '停止失败：' + (error.response?.data?.message || error.message), 6000);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelected(leads.map((l) => l.id));
  };

  const selectNone = () => {
    setSelected([]);
  };

  const handleImportSelected = async () => {
    if (selected.length === 0) {
      showMessage('error', '请先勾选要导入的线索');
      return;
    }
    await doImport({ ids: selected, tags: 'cos,uk-immigration' });
  };

  const handleImportAllWithEmail = async () => {
    await doImport({ only_with_email: true, tags: 'cos,uk-immigration' });
  };

  const doImport = async (payload) => {
    setImporting(true);
    try {
      const res = await leadAPI.import(payload);
      const r = res.data?.data || {};
      showMessage(
        'success',
        `导入完成：新增收件人 ${r.recipients_created || 0}，新增公司 ${r.companies_created || 0}，跳过重复 ${r.skipped_duplicates || 0}`,
        6000
      );
      setSelected([]);
      loadList();
    } catch (error) {
      showMessage('error', '导入失败：' + (error.response?.data?.message || error.message), 6000);
    } finally {
      setImporting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('确定要清空所有候选线索吗？此操作不可撤销。')) return;
    try {
      await leadAPI.clear();
      showMessage('success', '已清空候选线索');
      setSelected([]);
      loadList();
    } catch (error) {
      showMessage('error', '清空失败：' + (error.response?.data?.message || error.message), 6000);
    }
  };

  const progressPct =
    status.total > 0 ? Math.min(100, Math.round((status.done / status.total) * 100)) : 0;

  const allSelected = leads.length > 0 && selected.length === leads.length;

  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>线索查找器</h1>
        <p>查找英国移民律所线索，导入后用于雇主担保 / CoS 邮件营销</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      {/* 说明 */}
      <div className="alert alert-info">
        默认关键词偏向雇主担保 / 工签——抢 CoS 痛点最强的人群；导入后到“收件人管理”和 CoS 活动里发送。
      </div>

      {/* 查找表单 */}
      <div className="card">
        <div className="card-header">
          <h2>开始查找</h2>
        </div>

        <form onSubmit={handleStart}>
          <div className="form-group">
            <label>Google Places API Key（可选）</label>
            <input
              type="text"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder="留空则使用已保存的 Key，也可在「设置」中保存后长期使用"
            />
            <p style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
              提示：API Key 可在「设置」页面保存，保存后此处可留空。
            </p>
          </div>

          <div className="form-group">
            <label>城市（用逗号分隔）</label>
            <input
              type="text"
              value={form.cities}
              onChange={(e) => setForm({ ...form, cities: e.target.value })}
              placeholder="London, Manchester, Birmingham, Leeds, Glasgow"
            />
          </div>

          <div className="form-group">
            <label>关键词（用逗号分隔）</label>
            <input
              type="text"
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              placeholder="immigration solicitors, sponsor licence solicitors"
            />
          </div>

          <div className="form-group">
            <label>每个查询最大数量</label>
            <input
              type="number"
              min="1"
              value={form.max_per_query}
              onChange={(e) => setForm({ ...form, max_per_query: e.target.value })}
              placeholder="8"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={form.scrape_emails}
                onChange={(e) => setForm({ ...form, scrape_emails: e.target.checked })}
                style={{ width: 'auto' }}
              />
              抓取网站邮箱（scrape emails）
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={status.running}>
              开始查找
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleStop}
              disabled={!status.running}
            >
              停止
            </button>
          </div>
        </form>

        {/* 进度 */}
        {(status.running || status.total > 0 || status.error) && (
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
              <span>
                {status.running ? '查找中...' : '已停止'}
                {status.progress ? `（${status.progress}）` : ''}
              </span>
              <span>
                进度 {status.done}/{status.total} · 已找到 {status.found}
              </span>
            </div>
            <div style={{ background: '#eee', borderRadius: '5px', height: '14px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${progressPct}%`,
                  height: '100%',
                  background: status.error ? '#e74c3c' : '#3498db',
                  transition: 'width 0.3s',
                }}
              />
            </div>
            {status.error && (
              <p style={{ marginTop: '8px', fontSize: '13px', color: '#e74c3c' }}>
                错误：{status.error}
              </p>
            )}
          </div>
        )}
      </div>

      {/* 候选线索 */}
      <div className="card">
        <div className="card-header">
          <h2>候选线索</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="badge badge-info">新线索 {counts.new || 0}</span>
            <span className="badge badge-success">有邮箱 {counts.with_email || 0}</span>
            <span className="badge badge-secondary">已导入 {counts.imported || 0}</span>
          </div>
        </div>

        <div style={{ padding: '0 0 20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={allSelected ? selectNone : selectAll}>
            {allSelected ? '全不选' : '全选'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImportSelected}
            disabled={importing || selected.length === 0}
          >
            导入选中{selected.length > 0 ? `（${selected.length}）` : ''}
          </button>
          <button
            className="btn btn-success"
            onClick={handleImportAllWithEmail}
            disabled={importing}
          >
            只导入有邮箱的全部
          </button>
          <button
            className="btn btn-danger"
            onClick={handleClear}
            disabled={importing || leads.length === 0}
          >
            清空
          </button>
        </div>

        {leads.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            暂无候选线索，请先在上方开始查找
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={allSelected ? selectNone : selectAll}
                  />
                </th>
                <th>律所名称</th>
                <th>邮箱</th>
                <th>网站</th>
                <th>城市</th>
                <th>关键词</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(lead.id)}
                      onChange={() => toggleOne(lead.id)}
                    />
                  </td>
                  <td>{lead.firm_name || '-'}</td>
                  <td>
                    {lead.email ? (
                      lead.email
                    ) : (
                      <span style={{ color: '#bbb' }}>无</span>
                    )}
                  </td>
                  <td>
                    {lead.website ? (
                      <a href={lead.website} target="_blank" rel="noopener noreferrer">
                        {lead.website}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{lead.city || '-'}</td>
                  <td>{lead.keyword || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default LeadFinder;
